import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  inject,
  NgZone
} from '@angular/core';
import { SharedModule } from '../../shared/shared.module';
import { TranslateService } from '@ngx-translate/core';
import { IAppDashboard, IAppDashboardTypes, IAppPluginWidget, IPermissionsFlags, IPluginPermissions, ModelService, PluginInjectorService, PluginInjectorWidget } from 'core-client/api';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSelectChange } from '@angular/material/select'
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { WidgetHeaderOption } from './phone-widget-settings.interface';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import * as FontAwesomeIcons from '@fortawesome/free-solid-svg-icons';
import { SipService } from '../../shared/services/sip/sip.service';
import { JabraService } from '../../shared/services/jabra/jabra.service';
import { AudioDeviceService } from '../../shared/services/audio-device/audio-device.service';
import { ToneService } from '../../shared/services/tone/tone.service';
import { IDevice } from '@gnaudio/jabra-js/browser-esm/index.js';
import { Subscription } from 'rxjs';

@Component({
    selector: 'lib-phone-widget-settings',
    imports: [SharedModule],
    templateUrl: './phone-widget-settings.component.html',
    styleUrl: './phone-widget-settings.component.scss'
})
export class PhoneWidgetSettingsComponent implements OnInit, OnDestroy {
  protected readonly pluginInjectorService = inject(PluginInjectorService);
  protected readonly modelService = inject(ModelService);
  protected readonly translate = inject(TranslateService);
  protected readonly dialogRef: MatDialogRef<PhoneWidgetSettingsComponent, any> = inject(MatDialogRef<PhoneWidgetSettingsComponent>);

  // Dashboard Type (initialize the type of the currently active dashboard as 'view' which has the fewest permissions)
  dashboardType: IAppDashboardTypes = 'view';

  // Permissions
  permissions = {
    edit: false
  };

  // Phone widget data
  protected phoneWidget: IAppPluginWidget;
  widgetHeaderForm!: FormGroup;
  widgetHeaderOption: string = 'alwaysDisplayed';
  widgetTitle: string;
  showWidgetTitle: boolean = false;
  widgetId: string;

  // Font Awesome icons
  faHeadset: IconDefinition = FontAwesomeIcons.faHeadset;
  faMicrophoneIcon: IconDefinition = FontAwesomeIcons.faMicrophone;
  faHeadphones: IconDefinition = FontAwesomeIcons.faHeadphones;
  faVolumeUp: IconDefinition = FontAwesomeIcons.faVolumeUp;
  faRecordVinyl: IconDefinition = FontAwesomeIcons.faRecordVinyl;
  faStop: IconDefinition = FontAwesomeIcons.faStop;
  faRedo: IconDefinition = FontAwesomeIcons.faRedo;
  faPlayCircle: IconDefinition = FontAwesomeIcons.faPlayCircle;
  faPlug: IconDefinition = FontAwesomeIcons.faPlug;
  faUnlink: IconDefinition = FontAwesomeIcons.faUnlink;
  faBell: IconDefinition = FontAwesomeIcons.faBell;

  // Device-related properties
  audioInputDevices: MediaDeviceInfo[] = [];
  audioOutputDevices: MediaDeviceInfo[] = [];
  selectedAudioInputId: string = '';
  selectedAudioOutputId: string = '';
  selectedRingingDeviceId: string = '';
  isAudioOutputSelectionSupported: boolean = false;

  // Default device info
  defaultMicrophoneLabel: string = '';
  defaultSpeakerLabel: string = '';
  defaultInputDeviceId: string = '';  // ID of the likely default input device
  defaultOutputDeviceId: string = ''; // ID of the likely default output device
  allAudioInputDevices: MediaDeviceInfo[] = []; // including default/communications
  allAudioOutputDevices: MediaDeviceInfo[] = []; // including default/communications

  // Audio visualization and testing
  microphoneLevel: number = 0;
  readonly NUM_SEGMENTS = 10;
  audioSegments: number[] = Array(this.NUM_SEGMENTS).fill(0).map((_, i) => i);
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private microphoneStream: MediaStream | null = null;
  private visualizerAnimationFrame: number | null = null;
  isTestingMicrophone: boolean = false;
  isTestingSpeaker: boolean = false;
  isTestingRingingTone: boolean = false;

  // Recording and playback
  isRecording: boolean = false;
  audioRecorder: MediaRecorder | null = null;
  recordedChunks: Blob[] = [];
  recordedAudioUrl: string | null = null;
  recordingDuration: number = 0;
  private recordingTimer: any;

  // Default device options
  useDefaultMicrophone: boolean = false;
  useDefaultSpeaker: boolean = false;
  useDefaultRingingDevice: boolean = false;

  // Jabra headset integration
  jabra = {
    initialized: false,
    connected: false,
    deviceName: null as string | null,
    muted: false,
    onHold: false
  };
  jabraDeviceList: IDevice[] = [];

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) protected readonly data: PluginInjectorWidget.IWidget,
    private sipService: SipService,
    private audioDeviceService: AudioDeviceService,
    private jabraService: JabraService,
    private toneService: ToneService,
    private ngZone: NgZone
  ) {
    this.phoneWidget = data.appPluginWidget;
    this.widgetId = data.appPluginWidget._id;
    this.widgetTitle = data.appPluginWidget.name;
    this.showWidgetTitle = data.appPluginWidget.settings.showWidgetTitle;
    const widgetHeader = this.data.appPluginWidget.settings.widgetHeader as Record<string, boolean>;
    const selectedHeaderOption = Object.keys(widgetHeader).filter(
        (key: string) => widgetHeader[key]
    );
    this.widgetHeaderOption = selectedHeaderOption[0];
  }

  async ngOnInit(): Promise<void> {
    await this.updatePermissions();

    // Do not initialize / show widget header settings, if phone edit permission is not given
    if (this.permissions.edit) {
      this.widgetHeaderForm = this.initializeWidgetHeaderForm();
    }

    // Initialize audio device state
    this.initializeAudioDevices();

    // Initialize Jabra headset integration
    await this.initializeJabra();

    // Start audio visualization
    this.startAudioVisualization();
  }

  private async updatePermissions(): Promise<void> {
    const currentUser = this.modelService._user.getUser();
    const appDashboard = currentUser.app_dashboards.find(
      (dashboard: IAppDashboard) => dashboard._id === currentUser.active_dashboard
    );

    if (appDashboard) {
      this.dashboardType = appDashboard.type
      const pluginPermissions = await this.pluginInjectorService.permissionService.getPermissions(
        appDashboard, currentUser
      );

      const accessRights = pluginPermissions.find(
        (plugin_permission: IPluginPermissions) => plugin_permission.plugin_id === this.data.pluginId
      );

      if (accessRights?.widget_permissions) {
        const modulePermissions = accessRights.widget_permissions[this.data.pluginModule.toLowerCase()] as IPermissionsFlags;
        this.permissions = {
          edit: modulePermissions['astimax_phone_edit']
        };
      }
    }
  }

  isViewDashboard(): boolean {
    return this.dashboardType === 'view';
  }

  private initializeWidgetHeaderForm() {
    const widgetHeaderOption = (Object.keys(this.phoneWidget.settings.widgetHeader)
      .find((value: string) => this.phoneWidget.settings.widgetHeader[value as WidgetHeaderOption]) ?? 'alwaysDisplayed') as WidgetHeaderOption

    return new FormGroup({
      _id: new FormControl<string>(this.phoneWidget._id, {
        nonNullable: true
      }),
      name: new FormControl<string>(this.phoneWidget.name,{
        validators: [Validators.required, Validators.minLength(1)],
        nonNullable: true
      }),
      settings: new FormGroup({
        widgetHeaderOption: new FormControl<string>(widgetHeaderOption, {
          nonNullable: true
        }),
        showWidgetTitle: new FormControl<boolean>(this.phoneWidget.settings.showWidgetTitle, {
          nonNullable: true
        })
      })
    })
  }

  selectHeaderOption($event: MatSelectChange): void {
    const headerOptions: Record<string, { alwaysDisplayed: boolean; onHoverDisplayed: boolean; alwaysOff: boolean; transparent: boolean }> = {
      alwaysDisplayed: { alwaysDisplayed: true, onHoverDisplayed: false, alwaysOff: false, transparent: false },
      onHoverDisplayed: { alwaysDisplayed: false, onHoverDisplayed: true, alwaysOff: false, transparent: false },
      alwaysOff: { alwaysDisplayed: false, onHoverDisplayed: false, alwaysOff: true, transparent: false },
      transparent: { alwaysDisplayed: false, onHoverDisplayed: false, alwaysOff: false, transparent: true }
    };

    if ($event.value in headerOptions) {
      this.phoneWidget.settings.widgetHeader = headerOptions[$event.value];
    }
  }

  getWidgetHeaderDisplayOptionText(key: unknown): string {
    const translations: Record<string, string> = {
      alwaysDisplayed: 'ASTIMAX.PHONE.WIDGET.SETTINGS.WIDGET_HEADER.DISPLAY_OPTIONS.VALUES.ALWAYS_DISPLAYED',
      onHoverDisplayed: 'ASTIMAX.PHONE.WIDGET.SETTINGS.WIDGET_HEADER.DISPLAY_OPTIONS.VALUES.ON_HOVER_DISPLAYED',
      alwaysOff: 'ASTIMAX.PHONE.WIDGET.SETTINGS.WIDGET_HEADER.DISPLAY_OPTIONS.VALUES.ALWAYS_OFF',
      transparent: 'ASTIMAX.PHONE.WIDGET.SETTINGS.WIDGET_HEADER.DISPLAY_OPTIONS.VALUES.TRANSPARENT'
    };

    return this.translate.instant(translations[key as string] || 'ASTIMAX.PHONE.WIDGET.SETTINGS.WIDGET_HEADER.DISPLAY_OPTIONS.VALUES.UNKNOWN');
  }

  isValidForm(): boolean {
    if (this.widgetHeaderForm) {
      return !this.widgetHeaderForm.valid
    }
    return false
  }

  async onSubmit(): Promise<void> {
    if (!this.phoneWidget) {
      return;
    }

    this.phoneWidget.settings.showWidgetTitle = this.widgetHeaderForm.get('settings.showWidgetTitle')?.value;

    await this.modelService._appPluginWidget.upsertOne(this.phoneWidget);
    this.stopAudioVisualization();
    this.dialogRef.close();
  }

  onCloseDialog() {
    this.stopAudioVisualization();
    this.dialogRef.close('lib-phone-widget-settings');
  }

  // Device Settings Implementation

  private initializeAudioDevices(): void {
    // Setup audio device subscriptions
    this.subscriptions.push(
      // Raw device list with all devices
      this.audioDeviceService.getAvailableAudioInputDevices().subscribe(devices => {
        // Store all devices (including default/communications)
        this.allAudioInputDevices = devices;

        // Find the default device for display purposes
        this.identifyDefaultDevices();

        // Filter out devices with deviceId 'default' or 'communications' for the dropdown
        const filteredDevices = devices.filter(device =>
          device.deviceId !== 'default' && device.deviceId !== 'communications');
        this.audioInputDevices = filteredDevices;

        // Auto-select appropriate device if not using default
        if (!this.useDefaultMicrophone) {
          this.autoSelectInputDevice();
        }
      }),

      this.audioDeviceService.getAvailableAudioOutputDevices().subscribe(devices => {
        // Store all devices (including default/communications)
        this.allAudioOutputDevices = devices;

        // Find the default device for display purposes
        this.identifyDefaultDevices();

        // Filter out devices with deviceId 'default' or 'communications' for the dropdown
        const filteredDevices = devices.filter(device =>
          device.deviceId !== 'default' && device.deviceId !== 'communications');
        this.audioOutputDevices = filteredDevices;

        // Auto-select appropriate device if not using default
        if (!this.useDefaultSpeaker) {
          this.autoSelectOutputDevice();
        }

        // Auto-select appropriate ringing device if not using default
        if (!this.useDefaultRingingDevice) {
          this.autoSelectRingingDevice();
        }
      }),

      this.audioDeviceService.getCurrentAudioInputDevice().subscribe(deviceId => {
        this.selectedAudioInputId = deviceId;
        this.useDefaultMicrophone = deviceId === '';
      }),

      this.audioDeviceService.getCurrentAudioOutputDevice().subscribe(deviceId => {
        this.selectedAudioOutputId = deviceId;
        this.useDefaultSpeaker = deviceId === '';
      }),

      this.audioDeviceService.getCurrentRingingDevice().subscribe(deviceId => {
        this.selectedRingingDeviceId = deviceId;
        this.useDefaultRingingDevice = deviceId === '';
      }),

      this.audioDeviceService.getAudioOutputSelectionSupported().subscribe(supported => {
        this.isAudioOutputSelectionSupported = supported;
      })
    );
  }

  // Identify and set default device information
  private identifyDefaultDevices(): void {
    // For input devices (microphones)
    const defaultInputDevice = this.allAudioInputDevices.find(device => device.deviceId === 'default');
    if (defaultInputDevice && defaultInputDevice.label) {
      this.defaultMicrophoneLabel = defaultInputDevice.label;
      this.defaultInputDeviceId = defaultInputDevice.deviceId;
    } else {
      // Fallback if we can't determine the default device name
      this.defaultMicrophoneLabel = this.translate.instant('ASTIMAX.PHONE.WIDGET.SETTINGS.AUDIO_DEVICES.MICROPHONE.DEFAULT_MICROPHONE');
    }

    // For output devices (speakers)
    const defaultOutputDevice = this.allAudioOutputDevices.find(device => device.deviceId === 'default');
    if (defaultOutputDevice && defaultOutputDevice.label) {
      this.defaultSpeakerLabel = defaultOutputDevice.label;
      this.defaultOutputDeviceId = defaultOutputDevice.deviceId;
    } else {
      // Fallback if we can't determine the default device name
      this.defaultSpeakerLabel = this.translate.instant('ASTIMAX.PHONE.WIDGET.SETTINGS.AUDIO_DEVICES.SPEAKER.DEFAULT_SPEAKER');
    }
  }

  // Auto-select the most appropriate input device
  private autoSelectInputDevice(): void {
    if (this.audioInputDevices.length === 0) return;

    // First, check localStorage directly for saved preference
    const savedInputId = localStorage.getItem('preferredMicrophoneId');

    // Special handling for empty string (default device preference)
    if (savedInputId === '') {
      console.log('Using default input device (from saved preference)');
      this.selectedAudioInputId = '';
      this.useDefaultMicrophone = true;
      return;
    }

    // If we have a valid saved preference and it's available, use it
    if (savedInputId && savedInputId !== '' && this.audioInputDevices.some(d => d.deviceId === savedInputId)) {
      console.log('Using saved input device preference:', savedInputId);
      this.selectedAudioInputId = savedInputId;
      this.audioDeviceService.setAudioInputDevice(savedInputId);
      this.useDefaultMicrophone = false;
      return;
    }

    // If no valid saved preference, check current selection
    if (this.selectedAudioInputId && this.selectedAudioInputId !== '' &&
        this.audioInputDevices.some(d => d.deviceId === this.selectedAudioInputId)) {
      // Current selection is valid, keep it
      return;
    }

    // If using default device, set empty string
    if (this.useDefaultMicrophone) {
      this.selectedAudioInputId = '';
      this.audioDeviceService.setAudioInputDevice('');
      return;
    }

    // Otherwise, select first available device
    console.log('Auto-selecting first available input device');
    this.selectedAudioInputId = this.audioInputDevices[0].deviceId;
    this.audioDeviceService.setAudioInputDevice(this.audioInputDevices[0].deviceId);
  }

  // Auto-select the most appropriate output device
  private autoSelectOutputDevice(): void {
    if (this.audioOutputDevices.length === 0) return;

    // First, check localStorage directly for saved preference
    const savedOutputId = localStorage.getItem('preferredSpeakerId');

    // Special handling for empty string (default device preference)
    if (savedOutputId === '') {
      console.log('Using default output device (from saved preference)');
      this.selectedAudioOutputId = '';
      this.useDefaultSpeaker = true;
      return;
    }

    // If we have a valid saved preference and it's available, use it
    if (savedOutputId && savedOutputId !== '' && this.audioOutputDevices.some(d => d.deviceId === savedOutputId)) {
      console.log('Using saved output device preference:', savedOutputId);
      this.selectedAudioOutputId = savedOutputId;
      this.audioDeviceService.setAudioOutputDevice(savedOutputId);
      this.useDefaultSpeaker = false;
      return;
    }

    // If no valid saved preference, check current selection
    if (this.selectedAudioOutputId && this.selectedAudioOutputId !== '' &&
        this.audioOutputDevices.some(d => d.deviceId === this.selectedAudioOutputId)) {
      // Current selection is valid, keep it
      return;
    }

    // If using default device, set empty string
    if (this.useDefaultSpeaker) {
      this.selectedAudioOutputId = '';
      this.audioDeviceService.setAudioOutputDevice('');
      return;
    }

    // Otherwise, select first available device
    console.log('Auto-selecting first available output device');
    this.selectedAudioOutputId = this.audioOutputDevices[0].deviceId;
    this.audioDeviceService.setAudioOutputDevice(this.audioOutputDevices[0].deviceId);
  }

  // Auto-select the most appropriate ringing device
  private autoSelectRingingDevice(): void {
    if (this.audioOutputDevices.length === 0) return;

    // First, check localStorage directly for saved preference
    const savedRingingId = localStorage.getItem('preferredRingingDeviceId');

    // Special handling for empty string (default device preference)
    if (savedRingingId === '') {
      console.log('Using default ringing device (from saved preference)');
      this.selectedRingingDeviceId = '';
      this.useDefaultRingingDevice = true;
      return;
    }

    // If we have a valid saved preference and it's available, use it
    if (savedRingingId && savedRingingId !== '' && this.audioOutputDevices.some(d => d.deviceId === savedRingingId)) {
      console.log('Using saved ringing device preference:', savedRingingId);
      this.selectedRingingDeviceId = savedRingingId;
      this.audioDeviceService.setRingingDevice(savedRingingId);
      this.useDefaultRingingDevice = false;
      return;
    }

    // If no valid saved preference, check current selection
    if (this.selectedRingingDeviceId && this.selectedRingingDeviceId !== '' &&
        this.audioOutputDevices.some(d => d.deviceId === this.selectedRingingDeviceId)) {
      // Current selection is valid, keep it
      return;
    }

    // If using default device, set empty string
    if (this.useDefaultRingingDevice) {
      this.selectedRingingDeviceId = '';
      this.audioDeviceService.setRingingDevice('');
      return;
    }

    // Otherwise, select first available device
    console.log('Auto-selecting first available ringing device');
    this.selectedRingingDeviceId = this.audioOutputDevices[0].deviceId;
    this.audioDeviceService.setRingingDevice(this.audioOutputDevices[0].deviceId);
  }

  private async initializeJabra(): Promise<void> {
    try {
      await this.jabraService.initialize();

      // Set up Jabra subscriptions
      this.subscriptions.push(
        // SDK initialization status
        this.jabraService.isSdkInitialized$.subscribe(initialized => {
          this.ngZone.run(() => {
            this.jabra.initialized = initialized;
          });
        }),

        // Connected status
        this.jabraService.isConnected$.subscribe(connected => {
          this.ngZone.run(() => {
            this.jabra.connected = connected;
          });
        }),

        // Device name
        this.jabraService.deviceName$.subscribe(name => {
          this.ngZone.run(() => {
            this.jabra.deviceName = name;
          });
        }),

        // Device list
        this.jabraService.deviceList$.subscribe(devices => {
          this.ngZone.run(() => {
            this.jabraDeviceList = devices;
          });
        }),

        // Mute state
        this.jabraService.isMuted$.subscribe(muted => {
          this.ngZone.run(() => {
            this.jabra.muted = muted;
          });
        }),

        // Hold state
        this.jabraService.isOnHold$.subscribe(onHold => {
          this.ngZone.run(() => {
            this.jabra.onHold = onHold;
          });
        })
      );
    } catch (error) {
      console.error('Error initializing Jabra service:', error);
    }
  }

  /**
   * Pair a Jabra device via WebHID
   */
  async pairJabraDevice(): Promise<void> {
    try {
      await this.jabraService.pairDevice();
    } catch (error) {
      console.error('Error pairing Jabra device:', error);
    }
  }

  /**
   * Select a specific Jabra device from the list
   */
  async selectJabraDevice(device: IDevice): Promise<void> {
    try {
      await this.jabraService.connectDevice(device);
    } catch (error) {
      console.error('Error connecting to Jabra device:', error);
    }
  }

  /**
   * Disconnect the Jabra device and revoke HID access permissions
   */
  async disconnectJabraDevice(): Promise<void> {
    if (!this.jabra.connected) {
      console.warn('No Jabra headset connected to unpair');
      return;
    }

    try {
      // Reset the Jabra headset first to avoid the device remaining in an inconsistent state after disconnecting/unpairing
      await this.resetJabraHeadset();
      console.log('Disconnecting Jabra device and revoking HID access...');
      await this.jabraService.disconnectDevice();
      console.log('Jabra device unpaired');
    } catch (error) {
      console.error('Error disconnecting Jabra device:', error);
    }
  }

  /**
   * Reset the Jabra headset to its default state
   */
  async resetJabraHeadset(): Promise<void> {
    if (!this.jabra.connected) {
      console.warn('No Jabra headset connected to reset');
      return;
    }

    try {
      console.log('Resetting Jabra headset...');
      await this.jabraService.resetHeadset();
      console.log('Jabra headset reset completed');
    } catch (error) {
      console.error('Error resetting Jabra headset:', error);
    }
  }

  async changeAudioInputDevice(deviceId: string): Promise<void> {
    try {
      // If using default microphone, set deviceId to empty string
      const effectiveDeviceId = this.useDefaultMicrophone ? '' : deviceId;

      // Update the device via audio device service
      this.audioDeviceService.setAudioInputDevice(effectiveDeviceId);

      // Update active calls with the new device
      await this.sipService.updateAudioDevices();

      // Restart audio visualization with new device
      this.restartAudioVisualization();
    } catch (error) {
      console.error('Error changing audio input device:', error);
    }
  }

  async changeAudioOutputDevice(deviceId: string): Promise<void> {
    try {
      // If using default speaker, set deviceId to empty string
      const effectiveDeviceId = this.useDefaultSpeaker ? '' : deviceId;

      // Update the device via audio device service
      this.audioDeviceService.setAudioOutputDevice(effectiveDeviceId);

      // Update active calls with the new device
      await this.sipService.updateAudioDevices();
    } catch (error) {
      console.error('Error changing audio output device:', error);
    }
  }

  // Method for changing ringing tone device
  async changeRingingDevice(deviceId: string): Promise<void> {
    try {
      // If using default ringing device, set deviceId to empty string
      const effectiveDeviceId = this.useDefaultRingingDevice ? '' : deviceId;

      // Update the device via audio device service
      this.audioDeviceService.setRingingDevice(effectiveDeviceId);
    } catch (error) {
      console.error('Error changing ringing tone device:', error);
    }
  }

// Toggle default microphone option
toggleDefaultMicrophone(checked: boolean): void {
  this.useDefaultMicrophone = checked;

  if (checked) {
    // User checked the box - use default device
    this.changeAudioInputDevice('');
  } else {
    // User unchecked the box - need to select a specific device

    // Look for previously selected device (before checking "use default")
    const savedInputId = localStorage.getItem('preferredMicrophoneId');

    if (savedInputId && savedInputId !== '' && this.audioInputDevices.some(d => d.deviceId === savedInputId)) {
      // If there was a valid device selected before using default, go back to it
      this.selectedAudioInputId = savedInputId;
      this.audioDeviceService.setAudioInputDevice(savedInputId);
    } else if (this.audioInputDevices.length > 0) {
      // Otherwise, select the first available device
      this.selectedAudioInputId = this.audioInputDevices[0].deviceId;
      this.audioDeviceService.setAudioInputDevice(this.audioInputDevices[0].deviceId);
    }
  }
}

// Toggle default speaker option
toggleDefaultSpeaker(checked: boolean): void {
  this.useDefaultSpeaker = checked;

  if (checked) {
    // User checked the box - use default device
    this.changeAudioOutputDevice('');
  } else {
    // User unchecked the box - need to select a specific device

    // Look for previously selected device (before checking "use default")
    const savedOutputId = localStorage.getItem('preferredSpeakerId');

    if (savedOutputId && savedOutputId !== '' && this.audioOutputDevices.some(d => d.deviceId === savedOutputId)) {
      // If there was a valid device selected before using default, go back to it
      this.selectedAudioOutputId = savedOutputId;
      this.audioDeviceService.setAudioOutputDevice(savedOutputId);
    } else if (this.audioOutputDevices.length > 0) {
      // Otherwise, select the first available device
      this.selectedAudioOutputId = this.audioOutputDevices[0].deviceId;
      this.audioDeviceService.setAudioOutputDevice(this.audioOutputDevices[0].deviceId);
    }
  }
}

// Toggle default ringing device option
toggleDefaultRingingDevice(checked: boolean): void {
  this.useDefaultRingingDevice = checked;

  if (checked) {
    // User checked the box - use default device
    this.changeRingingDevice('');
  } else {
    // User unchecked the box - need to select a specific device

    // Look for previously selected device (before checking "use default")
    const savedRingingId = localStorage.getItem('preferredRingingDeviceId');

    if (savedRingingId && savedRingingId !== '' && this.audioOutputDevices.some(d => d.deviceId === savedRingingId)) {
      // If there was a valid device selected before using default, go back to it
      this.selectedRingingDeviceId = savedRingingId;
      this.audioDeviceService.setRingingDevice(savedRingingId);
    } else if (this.audioOutputDevices.length > 0) {
      // Otherwise, select the first available device
      this.selectedRingingDeviceId = this.audioOutputDevices[0].deviceId;
      this.audioDeviceService.setRingingDevice(this.audioOutputDevices[0].deviceId);
    }
  }
}

  // Method for testing ringing tone
  testRingingTone(event?: MouseEvent): void {
    // Stop event propagation to prevent panel closing
    if (event) {
      event.stopPropagation();
    }

    this.isTestingRingingTone = true;
    this.toneService.startRingingTone();

    // Stop the tone after 2 seconds
    setTimeout(() => {
      this.toneService.stopRingingTone();
      this.isTestingRingingTone = false;
    }, 2000);
  }

  // Audio visualization methods
  private async startAudioVisualization(): Promise<void> {
    try {
      // Stop any existing visualization
      this.stopAudioVisualization();

      // Create audio context
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

      // Get microphone stream using the audio device service
      this.microphoneStream = await this.audioDeviceService.getAudioStream(false);

      // Create analyzer
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;

      // Connect microphone to analyzer
      const source = this.audioContext.createMediaStreamSource(this.microphoneStream);
      source.connect(this.analyser);

      // Start visualization loop
      this.updateVisualization();
    } catch (error) {
      console.error('Error starting audio visualization:', error);
      this.microphoneLevel = 0;
    }
  }

  private updateVisualization(): void {
    if (!this.analyser) return;

    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(dataArray);

    // Calculate average level
    let sum = 0;
    for (const value of dataArray) {
      sum += value;
    }
    const average = sum / dataArray.length;

    // Convert to number of active segments (0 to NUM_SEGMENTS)
    this.microphoneLevel = Math.min(this.NUM_SEGMENTS, Math.floor(average * this.NUM_SEGMENTS / 256));

    // Continue animation loop
    this.visualizerAnimationFrame = requestAnimationFrame(() => this.updateVisualization());
  }

  private stopAudioVisualization(): void {
    // Cancel animation frame
    if (this.visualizerAnimationFrame) {
      cancelAnimationFrame(this.visualizerAnimationFrame);
      this.visualizerAnimationFrame = null;
    }

    // Stop microphone stream
    if (this.microphoneStream) {
      this.microphoneStream.getTracks().forEach(track => track.stop());
      this.microphoneStream = null;
    }

    // Close audio context
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }

    this.analyser = null;
    this.microphoneLevel = 0;
  }

  private restartAudioVisualization(): void {
    this.stopAudioVisualization();
    this.startAudioVisualization();
  }

  // Microphone testing methods
  async startMicrophoneTest(event?: MouseEvent): Promise<void> {
    // Stop event propagation to prevent panel closing
    if (event) {
      event.stopPropagation();
    }

    if (this.isRecording) return;

    try {
      this.isTestingMicrophone = true;
      this.isRecording = true;
      this.recordedChunks = [];
      this.recordingDuration = 0;

      // Get microphone stream using the audio device service
      const stream = await this.audioDeviceService.getAudioStream(false);

      // Create recorder
      this.audioRecorder = new MediaRecorder(stream);

      // Handle data available event
      this.audioRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      // Handle recording stop event
      this.audioRecorder.onstop = () => {
        // Create blob from recorded chunks
        const blob = new Blob(this.recordedChunks, { type: 'audio/webm' });

        // Create URL for playback
        if (this.recordedAudioUrl) {
          URL.revokeObjectURL(this.recordedAudioUrl);
        }
        this.recordedAudioUrl = URL.createObjectURL(blob);

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());

        this.isRecording = false;
      };

      // Start recording
      this.audioRecorder.start();

      // Start recording timer
      this.recordingTimer = setInterval(() => {
        this.recordingDuration += 1;
        // Auto-stop after 5 seconds
        if (this.recordingDuration >= 5) {
          this.stopMicrophoneTest();
        }
      }, 1000);

    } catch (error) {
      console.error('Error starting microphone test:', error);
      this.isTestingMicrophone = false;
      this.isRecording = false;
    }
  }

  stopMicrophoneTest(): void {
    // Stop recording
    if (this.audioRecorder && this.isRecording) {
      this.audioRecorder.stop();
    }

    // Clear timer
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }

    this.isRecording = false;
  }

  playRecordedAudio(event?: MouseEvent): void {
    // Stop event propagation to prevent panel closing
    if (event) {
      event.stopPropagation();
    }

    if (!this.recordedAudioUrl) return;

    const audio = new Audio(this.recordedAudioUrl);
    audio.play();
  }

  resetMicrophoneTest(event?: MouseEvent): void {
    // Stop event propagation to prevent panel closing
    if (event) {
      event.stopPropagation();
    }

    this.isTestingMicrophone = false;
    this.stopMicrophoneTest();

    // Clean up recorded audio
    if (this.recordedAudioUrl) {
      URL.revokeObjectURL(this.recordedAudioUrl);
      this.recordedAudioUrl = null;
    }

    this.recordedChunks = [];
    this.recordingDuration = 0;
  }

  // Speaker testing methods
  startSpeakerTest(event?: MouseEvent): void {
    // Stop event propagation to prevent panel closing
    if (event) {
      event.stopPropagation();
    }

    this.isTestingSpeaker = true;
    this.toneService.playSpeakerTestTone();

    // Reset flag after 1 second
    setTimeout(() => {
      this.isTestingSpeaker = false;
    }, 1000);
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());

    // Stop audio visualization
    this.stopAudioVisualization();

    // Stop microphone test
    this.stopMicrophoneTest();

    // Clean up recorded audio
    if (this.recordedAudioUrl) {
      URL.revokeObjectURL(this.recordedAudioUrl);
    }
  }
}
